import { useContext } from 'react'
import PropTypes from 'prop-types'

import {
  Button, Form, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import ProductSelector from '@components/ProductSelector'

import useProductSelector from '@components/ProductSelector/useProductSelector'
import useCreateOrEditProviderOrderCodeModal from './hooks/useCreateOrEditProviderOrderCodeModal'

const createOrUpdateProviderOrderCode = (entityState, props, createFn, updateFn) => {
  const {
    callbacks: { closeModal, createProviderOrderCode, updateProviderOrderCode },
  } = props

  const text = entityState.id ? 'Updated' : 'Created'
  const actionFn = entityState.id ? updateFn : createFn

  const providerOrderCodePayload = {
    callbacks: {
      action: actionFn,
      afterAction: closeModal,
    },
    entityParams: entityState,
    toastText: `Order Code ${text}`,
  }

  if (entityState.id) return updateProviderOrderCode(providerOrderCodePayload)

  return createProviderOrderCode(providerOrderCodePayload)
}

const CreateOrEditProviderOrderCodeModal = (props) => {
  const { callbacks, modalKey, showModal } = props

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, modalKey, {})
  const { providerOrderCode } = modalPayload

  const productSelectorPayload = useProductSelector(providerOrderCode)
  const { state } = productSelectorPayload

  const {
    callbacks: {
      createProviderOrderCode: createFn,
      updateProviderOrderCode: updateFn,
    },
    creating,
    entityState,
    entityState: {
      provider_code,
    },
    errors,
    handlers,
    providerId,
    saveEnabled,
    showIdentityCodeField,
    updating,
  } = useCreateOrEditProviderOrderCodeModal({ providerOrderCode, state })

  const newRecord = !providerOrderCode?.id

  const saveOrCreateButtonText = newRecord ? 'Add Order Code' : 'Save Order Code'

  const saveButtonEnabled = newRecord ? saveEnabled : !!provider_code

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={modalKey} showModal={showModal} size="small">
      <SidebarModal.Header callbacks={callbacks} title={newRecord ? 'Add' : 'Edit'} titleSecondLine="Order Code" />

      <SidebarModal.Content>
        <Form>
          {newRecord && (
            <ProductSelector
              productSelectorPayload={productSelectorPayload}
              providerId={providerId}
            />
          )}

          {(!newRecord || showIdentityCodeField) && (
            <Form.Field
              errorMessage={errors.provider_code}
              label="* Identity Code"
              marginTop={newRecord ? 'large' : null}
            >
              <input name="provider_code" value={provider_code} type="text" {...handlers} />
            </Form.Field>
          )}
        </Form>
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={!saveButtonEnabled}
          loading={creating || updating}
          onClick={() => createOrUpdateProviderOrderCode(entityState, props, createFn, updateFn)}
          size="large"
        >
          {saveOrCreateButtonText}
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

CreateOrEditProviderOrderCodeModal.propTypes = {
  callbacks: PropTypes.object.isRequired,
  modalKey: PropTypes.string,
  showModal: PropTypes.bool,
}

CreateOrEditProviderOrderCodeModal.defaultProps = {
  modalKey: 'CreateOrEditProviderOrderCodeModal',
}

const LazyLoadedModal = props => (
  <SidebarModal.RenderController {...props}>
    <CreateOrEditProviderOrderCodeModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
